import { FILTER_MATCH_MODES } from '../../../constants';
import { DatasetItem } from '../../../types/types';
import { isEmptyValue } from '../../../utils/managerUtils';
import { TranslateFn } from '../../../hooks/context/useTranslate';
import { ReportDataSource } from '../types';
import { formatTypedPrintValue } from './typedValueFormat';

export type FilterDescription = {
  label: string;
  /** Match mode in words, e.g. "obsahuje". Empty when it adds nothing. */
  relation: string;
  value: string;
  /** Item name, so a report can pick out one filter by column. */
  item: string;
  /** Present when the API parameter differs from the visible column name. */
  filterParam?: string;
  /** Other legacy column names mapped to the same parameter. */
  itemAliases?: string[];
  /** True for the filter actually applied to a source grid's rows. */
  sourceFilter?: boolean;
  /** Dataset whose filter this is; item names are not globally unique. */
  source: ReportDataSource;
};

const SUBSET_GROUP_MARKER = '>';
const VALUELESS_MATCH_MODES = ['isEmpty', 'isNotEmpty'];

/**
 * Match mode in words, from the catalogue the filter UI itself uses, so a
 * printed report says the same thing the screen said. 'equals' is left out: "Sklad
 * Praha" reads better than "Sklad rovná se Praha".
 */
const resolveRelationKey = (
  matchMode: string,
  itemType?: string
): string | undefined => {
  if (matchMode === 'equals' || matchMode === 'in' || matchMode === 'custom') {
    return undefined;
  }

  if (
    matchMode === 'lte' &&
    ['date', 'time', 'dateTime'].includes(itemType ?? '')
  ) {
    return 'print.filterRelation.until';
  }
  if (
    matchMode === 'gte' &&
    ['date', 'time', 'dateTime'].includes(itemType ?? '')
  ) {
    return 'print.filterRelation.since';
  }
  if (matchMode === 'notEquals' && itemType === 'time') {
    return 'print.filterRelation.except';
  }

  const keys: Record<string, string> = {
    endsWith: 'print.filterRelation.endsWith',
    startsWith: 'print.filterRelation.startsWith',
    contains: 'print.filterRelation.contains',
    notContains: 'print.filterRelation.notContains',
    notEquals: 'print.filterRelation.notEquals',
    notIn: 'print.filterRelation.notContains',
    lt: 'print.filterRelation.lessThan',
    lte: 'print.filterRelation.lessOrEqual',
    gt: 'print.filterRelation.greaterThan',
    gte: 'print.filterRelation.greaterOrEqual',
    dateIs: 'print.filterRelation.is',
    dateTo: 'print.filterRelation.until',
    dateFrom: 'print.filterRelation.since',
    between: 'print.filterRelation.between',
    dateIsNot: 'print.filterRelation.except',
    'datetime-equals': 'print.filterRelation.is',
    'datetime-lte': 'print.filterRelation.until',
    'datetime-gte': 'print.filterRelation.since',
    'datetime-between': 'print.filterRelation.between',
    'datetime-notEquals': 'print.filterRelation.except',
    isEmpty: 'print.filterRelation.isEmpty',
    isNotEmpty: 'print.filterRelation.isNotEmpty',
  };
  return keys[matchMode];
};

const resolveRelation = (
  matchMode: string,
  itemType?: string,
  t?: TranslateFn
): string => {
  const key = resolveRelationKey(matchMode, itemType);
  if (!key) {
    return '';
  }
  if (t) {
    const translated = t(key);
    if (translated !== key) {
      return translated.toLowerCase();
    }
  }

  return (
    FILTER_MATCH_MODES.find(
      (mode) => mode.name === matchMode
    )?.label.toLowerCase() ?? ''
  );
};

const formatFilterValue = (
  value: unknown,
  itemType?: string,
  locale?: string
): string => {
  if (Array.isArray(value)) {
    return value
      .filter((entry) => !isEmptyValue(entry))
      .map((entry) => formatFilterValue(entry, itemType, locale))
      .join(', ');
  }

  const typedValue = formatTypedPrintValue(value, itemType, undefined, locale);
  if (typedValue !== null) {
    return typedValue;
  }

  if (value instanceof Date) {
    return value.toLocaleDateString(locale);
  }

  if (typeof value === 'boolean') {
    return locale?.toLowerCase().startsWith('en')
      ? value
        ? 'Yes'
        : 'No'
      : value
        ? 'Ano'
        : 'Ne';
  }

  if (value !== null && typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
};

/**
 * Describes the filters the printed rows were selected by, in the words the
 * filter UI uses, so a report states what it covers.
 *
 * Filters on subordinate views (keyed '<datasetId>>') are included: leaving them
 * out would let a filtered report claim it was unfiltered.
 *
 * activeFilters shape is { [itemName]: { [matchMode]: value } }.
 */
export const describeActiveFilters = (
  activeFilters: Record<string, unknown> | null,
  items: DatasetItem[],
  t?: TranslateFn,
  locale?: string,
  itemsBySource?: Record<string, DatasetItem[]>,
  activeFiltersBySource?: Record<string, Record<string, unknown>>
): FilterDescription[] => {
  if (!activeFilters && !activeFiltersBySource) {
    return [];
  }

  const descriptions: FilterDescription[] = [];

  const describe = (
    itemName: string,
    entry: unknown,
    source: ReportDataSource,
    sourceFilter = false
  ): void => {
    if (!entry || typeof entry !== 'object') {
      return;
    }
    const [matchMode, value] =
      Object.entries(entry as Record<string, unknown>)[0] ?? [];
    if (matchMode === undefined || isEmptyValue(value)) {
      return;
    }

    const sourceItems = itemsBySource?.[source] ?? items;
    const matchingItems = sourceItems.filter(
      (candidate) =>
        candidate.filterParam === itemName || candidate.name === itemName
    );
    const item = matchingItems[0];
    const resolvedItemName = item?.name ?? itemName;
    const resolvedFilterParam = item?.filterParam ?? itemName;
    const itemAliases = matchingItems
      .map((candidate) => candidate.name)
      .filter((name) => name !== resolvedItemName);
    descriptions.push({
      label: item?.label ?? itemName,
      relation: resolveRelation(matchMode, item?.type, t),
      // The filters UI submits a boolean `true` sentinel for these two modes so
      // the filter survives empty-value stripping. It is transport metadata,
      // not text that belongs in a human-readable report condition.
      value: VALUELESS_MATCH_MODES.includes(matchMode)
        ? ''
        : formatFilterValue(value, item?.type, locale),
      item: resolvedItemName,
      ...(resolvedFilterParam !== resolvedItemName
        ? { filterParam: resolvedFilterParam }
        : {}),
      ...(itemAliases.length > 0 ? { itemAliases } : {}),
      ...(sourceFilter ? { sourceFilter: true } : {}),
      source,
    });
  };

  Object.entries(activeFilters ?? {}).forEach(([key, entry]) => {
    if (!entry || typeof entry !== 'object') {
      return;
    }

    if (key.endsWith(SUBSET_GROUP_MARKER)) {
      const source = `subset:${key.slice(
        0,
        -SUBSET_GROUP_MARKER.length
      )}` as ReportDataSource;
      Object.entries(entry as Record<string, unknown>).forEach(
        ([field, nested]) => describe(field, nested, source)
      );
      return;
    }

    describe(key, entry, 'master');
  });

  Object.entries(activeFiltersBySource ?? {}).forEach(([source, filters]) => {
    Object.entries(filters).forEach(([itemName, entry]) =>
      describe(itemName, entry, source as ReportDataSource, true)
    );
  });

  return descriptions;
};

/** One filter as a sentence: "Název obsahuje pizza". */
export const formatFilterDescription = (filter: FilterDescription): string =>
  [filter.label, filter.relation, filter.value].filter(Boolean).join(' ');

/** Value of one named filter, for a header that states the warehouse or branch. */
export const findFilterValue = (
  filters: FilterDescription[],
  itemName: string | undefined,
  source?: ReportDataSource
): FilterDescription | undefined =>
  itemName
    ? filters.find(
        (filter) =>
          filter.sourceFilter && matchesFilter(filter, itemName, source)
      ) ?? filters.find((filter) => matchesFilter(filter, itemName, source))
    : undefined;

const matchesFilter = (
  filter: FilterDescription,
  itemName: string,
  source?: ReportDataSource
): boolean =>
  (filter.item === itemName ||
    filter.filterParam === itemName ||
    filter.itemAliases?.includes(itemName) === true) &&
  (!source || filter.source === source);
